import {useAuthEffect, useIsAuthenticatedValue} from '@/auth/hooks/useIsAuthenticated'
import {CART_KEY, migrateCartV1ToSupportCountry} from '@/lib/cart/cartDb'
import {type SupportedLocales, useLocale} from '@/lib/locale/locale'
import queries from '@/queries/queries'
import {getCart} from '@/services/cartService'
import {isStorageEventOfKey} from '@/utils/simpleStorage'

function getCartQueryOptions(isAuthenticated: boolean, locale: SupportedLocales) {
  return queries.getQueryOptions({
    name: 'cart',
    params: [locale],
    queryFn: async ({signal}) => getCart({isAuthenticated, signal, locale}),
    refetchInterval: 100000, // every 100s
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  })
}

export default memo(function CartUpdater() {
  migrateCartV1ToSupportCountry()

  const queryClient = useQueryClient()
  const [locale] = useLocale()
  const isAuthenticated = useIsAuthenticatedValue()
  const queryOptions = getCartQueryOptions(isAuthenticated, locale)

  const invalidateCart = useCallback(() => {
    queryClient
      .invalidateQueries({
        queryKey: queries.getQueryKey('cart', [locale]),
        exact: true,
      })
      .catch(() => {
        // do nothing
      })
  }, [queryClient, locale])

  // Listen to simpleStorage events and invalidate cart query when cart is updated in simpleStorage
  useEffect(() => {
    const listener = (event: unknown) => {
      if (isStorageEventOfKey(CART_KEY, event)) {
        invalidateCart()
      }
    }

    const abortController = new AbortController()

    window.addEventListener('storage', listener, {signal: abortController.signal})
    window.addEventListener('simpleStorage', listener, {signal: abortController.signal})

    return () => {
      abortController.abort()
    }
  }, [invalidateCart])

  // invalidate cart when user sign out or sign in
  useAuthEffect(invalidateCart)

  useQuery(queryOptions)

  return null
})
